// Light and dark (BRAND.md §4, ADR-0041): the portal follows the device unless the player chose one. The choice is a
// per-device convenience kept in localStorage, which can be absent or refuse (a private window), so every read and
// write is guarded and the device's preference is the fallback.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'clavian.player.theme';

function readChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // The choice simply does not outlive this page.
  }
}

function systemMode(): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

interface ThemeValue {
  readonly choice: ThemeChoice;
  readonly mode: ThemeMode;
  readonly setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeValue>({ choice: 'system', mode: 'dark', setChoice: () => undefined });

export function ThemeChoiceProvider({ children }: { readonly children: (mode: ThemeMode) => ReactNode }): ReactNode {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => (typeof window === 'undefined' ? 'system' : readChoice()));
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (): void => setSystem(query.matches ? 'light' : 'dark');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode: ThemeMode = choice === 'system' ? system : choice;
  const value = useMemo<ThemeValue>(
    () => ({
      choice,
      mode,
      setChoice: (next) => {
        writeChoice(next);
        setChoiceState(next);
      },
    }),
    [choice, mode],
  );

  useEffect(() => {
    // The browser's own widgets (scrollbars, form controls) follow too.
    document.documentElement.style.colorScheme = mode;
    // The page's own ground (the html element's tokens) follows the theme, not only the app's root.
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  return <ThemeContext.Provider value={value}>{children(mode)}</ThemeContext.Provider>;
}

export function useThemeChoice(): ThemeValue {
  return useContext(ThemeContext);
}
